import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import TopNavigation from '../components/TopNavigation';

interface TermsAndConditionsPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToPrivacy?: () => void;
  onToggleMenu: () => void;
  isAuthenticated: boolean;
}

const TermsAndConditionsPage: React.FC<TermsAndConditionsPageProps> = ({ onNavigateToExplore, onNavigateToProfile, onNavigateToMyBooks, onNavigateToLibrary, onNavigateHome, onNavigateToLogin, onNavigateToSignup, onNavigateToPrivacy, onToggleMenu, isAuthenticated }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'var(--background-colour-dark-mode)' : 'var(--background-colour-light-mode)' }}>
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToMyBooks={onNavigateToMyBooks}
        showProfileIcon={isAuthenticated}
      />

      {/* Main Content - Single Column Layout */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        <div className="w-full max-w-5xl mx-auto">

          {/* Page Header */}
          <header className="text-left w-full my-24 max-w-5xl mx-auto">
          <h2 className={`portrait-text-app mb-4 text-7xl lg:text-8xl xl:text-9xl font-bold leading-tight line-height-.2 -mt-1 ${
            isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
          }`}>
            Terms and Conditions
          </h2>
          <p className={`text-xl ${
              isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
            }`}>
              Welcome to the beta version of our application on hi-aleph.com (the "App"), owned and operated by Delphi Art Technology Limited, a company registered in England (the "Company"). These Terms and Conditions ("Terms") govern your use of the App. By signing up and using the App, you agree to be bound by these Terms.
          </p>
          
          {/* Colored Element */}
          <div className="flex justify-end my-2">
            <div className={`w-48 h-20 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}></div>
          </div>
        </header>

          {/* Terms Content */}
          <div className={`prose prose-lg max-w-none pb-18 ${isDarkMode ? 'prose-invert' : ''}`}>
            <div className={`space-y-8 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  1. User Accounts
                </h2>
                <p className="text-lg leading-relaxed">
                  To use the App, you are required to sign up with a valid email address. You are responsible for maintaining the confidentiality of your account and are fully responsible for all activities that occur under your account.
                </p>
              </section>

              <section id="privacy">
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  2. Privacy
                </h2>
                <p className="text-lg leading-relaxed">
                  How we collect, use, disclose, and otherwise process your personal data is set out in our{' '}
                  <button
                    type="button"
                    onClick={() => onNavigateToPrivacy?.()}
                    className={`cursor-pointer underline hover:opacity-80 transition-opacity ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}
                  >
                    Privacy Notice
                  </button>
                  , which forms part of these Terms.
                </p>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  3. Communication
                </h2>
                <p className="text-lg leading-relaxed mb-4">
                  By signing up, you may be asked to provide your consent to receive communications from us. These communications may include:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>A questionnaire to gather your feedback on the App.</li>
                  <li>Information and marketing materials related to the App, such as updates, new features, and official launch announcements.</li>
                </ul>
                <p className="text-lg leading-relaxed mt-4">
                  You can opt out of receiving marketing communications at any time by following the unsubscribe link in the emails we send.
                </p>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  4. Intellectual Property
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className={`portrait-text-app text-xl font-semibold mb-3 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                      4.1 Ownership of the App
                    </h3>
                    <p className="text-lg leading-relaxed mb-4">
                      The App, including but not limited to its design, structure, software, code, graphics, user interface, functionality, and all related intellectual property rights, is and shall remain the exclusive property of Delphi Art Technology Limited. All rights, title, and interest in and to the App are owned by the Company. You are granted a limited, non-exclusive, non-transferable, revocable license to use the App solely for your personal, non-commercial use in accordance with these Terms.
                    </p>
                    <p className="text-lg leading-relaxed">
                      You may not copy, modify, distribute, sell, lease, or reverse engineer any part of the App without the express written permission of Delphi Art Technology Limited.
                    </p>
                  </div>

                  <div>
                    <h3 className={`portrait-text-app text-xl font-semibold mb-3 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                      4.2 User-Generated Content
                    </h3>
                    <p className="text-lg leading-relaxed">
                      Any content, feedback, suggestions, or materials you submit through the App may be used by the Company for the purpose of improving the App and developing new features, without any obligation to compensate you.
                    </p>
                  </div>

                  <div>
                    <h3 className={`portrait-text-app text-xl font-semibold mb-3 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                      4.3 Third-Party Content and Fair Use
                    </h3>
                    <p className="text-lg leading-relaxed">
                      The App may contain copyrighted material from third parties, the use of which has not always been specifically authorised by the copyright owner. We are making such material available in our efforts to advance understanding of various subjects. We believe this constitutes a "fair use" of any such copyrighted material as provided for in section 107 of the US Copyright Law. If you wish to use copyrighted material from the App for purposes of your own that go beyond "fair use," you must obtain permission from the copyright owner.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  5. Disclaimer of Warranties
                </h2>
                <p className="text-lg leading-relaxed">
                  The App is provided on an "AS IS" and "AS AVAILABLE" basis. We make no warranties, expressed or implied, regarding the operation or availability of the App, or the information, content, or materials included therein. You expressly agree that your use of the App is at your sole risk.
                </p>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  6. Limitation of Liability
                </h2>
                <p className="text-lg leading-relaxed">
                  In no event shall Delphi Art Technology Limited, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the App.
                </p>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  7. Changes to These Terms
                </h2>
                <p className="text-lg leading-relaxed">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on this page.
                </p>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  8. Governing Law and Jurisdiction
                </h2>
                <p className="text-lg leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising out of or in connection with these Terms or your use of the App shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
              </section>

              <section>
                <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>
                  9. Contact Us
                </h2>
                <p className="text-lg leading-relaxed">
                  If you have any questions about these Terms, please contact us on <a href="mailto:say@hi-aleph.com" className={`underline hover:opacity-80 transition-opacity ${isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'}`}>say@hi-aleph.com</a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
